// Copied verbatim from tab-portal src/lib/name-identification (qa c50f5268), the portal mirror of
// core-api src/utils/nameIdentification. Rules only: no AI pass. Keep the three in step.
import {
  AI_CONFIDENCE_THRESHOLD,
  LEGAL_SUFFIXES,
  ORG_DESCRIPTORS,
  PERSON_SUFFIXES,
} from './constants'
import {
  hasDigitInToken,
  isHyphenatedSurnameToken,
  isSingleLetterToken,
  normalizeName,
  normalizeNameKey,
  tokenize,
} from './normalize'
import { tokenLooksLikeGiven } from './parsePersonName'
import {
  ClassifyNameOptions,
  ClassifyNameResult,
  NameClassification,
} from './types'

const upperClean = (token: string): string =>
  token.toUpperCase().replace(/\./g, '')

const LEGAL_SUFFIX_PATTERNS = LEGAL_SUFFIXES.map(
  (suffix) => new RegExp(`\\b${suffix.replace(/\./g, '\\.')}\\b\\.?$`),
)

const endsWithLegalSuffix = (tokens: string[]): string | null => {
  if (tokens.length === 0) return null
  const joined = tokens.join(' ').toUpperCase()
  for (let i = 0; i < LEGAL_SUFFIXES.length; i += 1) {
    if (LEGAL_SUFFIX_PATTERNS[i].test(joined)) return LEGAL_SUFFIXES[i]
  }
  return null
}

/** Shortest descriptor prefix accepted as a truncated org word. */
const MIN_TRUNCATION_PREFIX = 3

/**
 * Find an organization descriptor token.
 *
 * Exact whole-token match anywhere in the name; a *prefix* match is accepted
 * only on the final token, which is where client files truncate (e.g.
 * "BELMONT RESEARCH ASSOCIATI", "BENEVOLENT HEALTHCARE FOU"). Matching prefixes
 * on any token would misclassify surnames — CO/COLLEGE would swallow COLLINS,
 * SERVICE would swallow SERVIN — so the position guard is load-bearing.
 */
const findOrgDescriptor = (tokens: string[]): string | null => {
  if (tokens.length === 0) return null
  const lastIndex = tokens.length - 1

  for (let i = 0; i < tokens.length; i += 1) {
    const upper = upperClean(tokens[i])
    for (const descriptor of ORG_DESCRIPTORS) {
      if (upper === descriptor) return descriptor

      // Truncated trailing word: token is a strict prefix of the descriptor.
      if (
        i === lastIndex &&
        upper.length >= MIN_TRUNCATION_PREFIX &&
        upper.length < descriptor.length &&
        descriptor.startsWith(upper) &&
        !tokenLooksLikeGiven(tokens[i])
      ) {
        return descriptor
      }
    }
  }
  return null
}

const hasBusinessMarkers = (raw: string): boolean =>
  /\bDBA\b/i.test(raw) || raw.includes('&') || /\(\s*THE\s*\)/i.test(raw)

const isAlphanumericBrand = (tokens: string[]): boolean =>
  tokens.some((t) => hasDigitInToken(t) && /[A-Z]/i.test(t))

const isPersonShape = (tokens: string[]): boolean => {
  if (tokens.length === 0) return false
  if (tokens.length === 1) return false
  if (tokens.length > 4) return false

  const lastToken = upperClean(tokens[tokens.length - 1])
  if (PERSON_SUFFIXES.has(lastToken)) {
    return isPersonShape(tokens.slice(0, -1))
  }

  if (tokens.length === 2) {
    const [a, b] = tokens
    return (
      isHyphenatedSurnameToken(a) ||
      tokenLooksLikeGiven(b) ||
      tokenLooksLikeGiven(a) ||
      isSingleLetterToken(b)
    )
  }

  if (tokens.length === 3) {
    const [a, b, c] = tokens
    if (isSingleLetterToken(b)) return true
    if (isSingleLetterToken(c)) return true
    if (isHyphenatedSurnameToken(a) && tokenLooksLikeGiven(b)) return true
    return tokenLooksLikeGiven(b) || tokenLooksLikeGiven(c)
  }

  return tokens.some((t) => tokenLooksLikeGiven(t))
}

/**
 * Apply the TIN-format hint.
 *
 * A formatted SSN (123-45-6789) means person and a formatted EIN (12-3456789)
 * means business — the IRS format is a fact about the row, not a guess about
 * the string, so it outranks every name heuristic. When the hint contradicts
 * the name signal the hint always wins for filing (`classification` = lean).
 * Strong name signals (e.g. LLC suffix) set `conflict` so the reviewer sees
 * the mismatch; `tinLocked` blocks the AI pass from overriding TIN format —
 * only a human reviewer can change it.
 */
const tinHintLabel = (kind: 'individual' | 'business'): string =>
  kind === 'individual' ? 'SSN' : 'EIN'

const tinHintPhrase = (
  kind: 'individual' | 'business',
  source: ClassifyNameOptions['tinHintSource'],
  verb: 'confirms' | 'indicates',
): string => {
  const label = tinHintLabel(kind)
  if (source === 'id-type') {
    return `Declared ${label} ${verb}`
  }
  return `${label} format ${verb}`
}

const applyTinHint = (
  result: ClassifyNameResult,
  options: ClassifyNameOptions,
): ClassifyNameResult => {
  const tinHint = options.tinHint
  if (!tinHint || tinHint === 'unknown') return result

  const lean: NameClassification =
    tinHint === 'individual' ? 'individual' : 'business'
  const source = options.tinHintSource

  if (result.classification === lean) {
    return {
      ...result,
      confidence: Math.min(0.99, Math.max(result.confidence, 0.97)),
      signal: `${result.signal}; ${tinHintPhrase(lean, source, 'confirms')} ${lean}`,
      tinLocked: true,
    }
  }

  const label = tinHintLabel(lean)
  return {
    classification: lean,
    confidence: 0.97,
    conflict: result.confidence >= 0.9,
    signal: `${tinHintPhrase(lean, source, 'indicates')} ${lean}; name looks like ${result.classification === 'individual' ? 'a person' : 'a business'} (${result.signal}) — kept as ${lean} per the ${label}`,
    tinLocked: true,
  }
}

/**
 * Deterministic person vs business classifier.
 * Returns confidence 0–1 and a human-readable signal (POC spreadsheet columns).
 */
export const classifyName = (
  raw: string,
  options: ClassifyNameOptions = {},
): ClassifyNameResult => {
  const normalized = normalizeName(raw)
  if (!normalized) {
    return {
      classification: 'individual',
      confidence: 0.5,
      signal: 'empty name',
    }
  }

  const tokens = tokenize(normalized)

  const legalSuffix = endsWithLegalSuffix(tokens)
  if (legalSuffix) {
    const brandNote = isAlphanumericBrand(tokens)
      ? '; brandable/alphanumeric name'
      : ''
    return applyTinHint(
      {
        classification: 'business',
        confidence: 0.99,
        signal: `corporate suffix '${legalSuffix}'${brandNote}`,
      },
      options,
    )
  }

  if (hasBusinessMarkers(normalized)) {
    return applyTinHint(
      {
        classification: 'business',
        confidence: 0.99,
        signal: 'business marker (&, DBA, or parenthetical THE)',
      },
      options,
    )
  }

  const orgDescriptor = findOrgDescriptor(tokens)
  if (orgDescriptor) {
    return applyTinHint(
      {
        classification: 'business',
        confidence: 0.94,
        signal: `firm/org descriptor '${orgDescriptor}' without legal suffix`,
      },
      options,
    )
  }

  if (isAlphanumericBrand(tokens) && tokens.length >= 2) {
    return applyTinHint(
      {
        classification: 'business',
        confidence: 0.95,
        signal: 'brandable/alphanumeric token',
      },
      options,
    )
  }

  if (tokens.length >= 4 && !isPersonShape(tokens)) {
    return applyTinHint(
      {
        classification: 'business',
        confidence: 0.94,
        signal: 'four or more tokens without person shape',
      },
      options,
    )
  }

  if (tokens.length === 3) {
    const [a, b, c] = tokens
    if (
      tokenLooksLikeGiven(a) &&
      tokenLooksLikeGiven(b) &&
      isSingleLetterToken(c)
    ) {
      return applyTinHint(
        {
          classification: 'individual',
          confidence: 0.94,
          signal: 'given name slightly ambiguous',
        },
        options,
      )
    }
    if (isHyphenatedSurnameToken(a) && tokenLooksLikeGiven(b)) {
      return applyTinHint(
        {
          classification: 'individual',
          confidence: 0.96,
          signal: 'hyphenated surname + given name pattern',
        },
        options,
      )
    }
    if (isSingleLetterToken(b) || isSingleLetterToken(c)) {
      return applyTinHint(
        {
          classification: 'individual',
          confidence: 0.97,
          signal: 'surname + given name + middle initial pattern',
        },
        options,
      )
    }
    if (tokenLooksLikeGiven(b) && tokenLooksLikeGiven(c)) {
      return applyTinHint(
        {
          classification: 'individual',
          confidence: 0.96,
          signal: 'surname + two given names pattern',
        },
        options,
      )
    }
  }

  if (tokens.length === 2) {
    const [a, b] = tokens
    if (isHyphenatedSurnameToken(a)) {
      return applyTinHint(
        {
          classification: 'individual',
          confidence: 0.96,
          signal: 'hyphenated surname + given name pattern',
        },
        options,
      )
    }
    if (tokenLooksLikeGiven(b) || tokenLooksLikeGiven(a)) {
      const bothGiven = tokenLooksLikeGiven(a) && tokenLooksLikeGiven(b)
      return applyTinHint(
        {
          classification: 'individual',
          confidence: bothGiven ? 0.94 : 0.96,
          signal: bothGiven
            ? 'given name slightly ambiguous'
            : 'surname + given name pattern',
        },
        options,
      )
    }
  }

  if (isPersonShape(tokens)) {
    return applyTinHint(
      {
        classification: 'individual',
        confidence: tokens.length >= 3 ? 0.97 : 0.95,
        signal: 'person name shape',
      },
      options,
    )
  }

  return applyTinHint(
    {
      classification: 'individual',
      confidence: 0.5,
      signal: 'no strong business or person signal',
    },
    options,
  )
}

/** Classify distinct non-empty names; preserves input order in output keys. */
export const classifyDistinctNames = (
  names: string[],
  options: ClassifyNameOptions = {},
): Map<string, ClassifyNameResult> => {
  const results = new Map<string, ClassifyNameResult>()
  for (const name of names) {
    // Case-insensitive key: "Smith John" and "SMITH JOHN" are one name.
    const key = normalizeNameKey(name)
    if (!key || results.has(key)) continue
    results.set(key, classifyName(name, options))
  }
  return results
}

/**
 * Names with confidence below threshold — candidates for AI second pass.
 * TIN/name conflicts are always included regardless of confidence.
 *
 * `tinLocked` names are excluded even if flagged as a conflict: the ID format
 * already decided the classification, so an AI opinion cannot change it and
 * sending the name over just costs latency for no benefit.
 */
export const namesNeedingAiPass = (
  results: Map<string, ClassifyNameResult>,
  threshold = AI_CONFIDENCE_THRESHOLD,
): string[] =>
  [...results.entries()]
    .filter(([, r]) => !r.tinLocked && (r.conflict || r.confidence < threshold))
    .map(([name]) => name)
